import { ItemLocationOrderSection } from '../components/ItemLocationOrderSection';
import { PageHeader } from '../components/PageHeader';
import { ErrorState } from '../components/ErrorState';
import { OrderFormLayout } from '../components/OrderFormLayout';
import { useEditEventMerchantOrderContext } from './EditEventMerchantOrderContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import i18n from '@/utils/i18n';
import { useHistory } from 'react-router';
import { Routes } from '@/utils/routes';
import { OrderInfo } from '../components/OrderInfo';
import { CostSummary } from '../components/CostSummary';
import { OrderField } from '../PublicEventOrderForm/components/OrderFormFields';
import { useIsMobile } from '@/hooks/use-mobile';
import { useMemo, useEffect, useState } from 'react';
import { SectionCard } from '../components/SectionCard';
import { getEventOrderDisabledStatuses } from '../components/orderStatusConfig';
import { FaCheckCircle } from 'react-icons/fa';
import { EventOrderBottomBar } from '../PublicEventOrderForm/components/EventOrderBottomBar';
import SvgIcon from '@/components/Icon/SvgIcon';
import { BottomBar } from '@/components/BottomBar/BottomBar';

type EditBottomBarProps = {
  isSubmitting: boolean;
  hasOrderChanged: boolean;
  onCancel: () => void;
  onSave: () => void;
};

function EditBottomBar({
  isSubmitting,
  hasOrderChanged,
  onCancel,
  onSave,
}: EditBottomBarProps) {
  const status = useMemo(() => {
    if (isSubmitting) return 'loading';
    if (hasOrderChanged) return 'has-changes';
    return 'no-changes';
  }, [isSubmitting, hasOrderChanged]);

  const content = useMemo(() => {
    if (status === 'loading') {
      return {
        text: i18n.t('Common:saving') || 'Saving...',
        icon: (
          <SvgIcon
            name="arrows-circle-refresh"
            size={20}
            className="tw:animate-spin"
          />
        ),
        button: <Button disabled>{i18n.t('Common:pleaseWait')}</Button>,
        className: 'tw:bg-gray-50',
      };
    }

    if (status === 'has-changes') {
      return {
        text:
          i18n.t('EventOrder:orderHasUnsavedChanges') ||
          'You have unsaved changes',
        icon: <SvgIcon name="warning-triangle" size={20} />,
        button: (
          <>
            <Button
              variant="destructive"
              onClick={onCancel}
              className="tw:dark:bg-red-500 tw:dark:text-white"
            >
              {i18n.t('Common:discardChanges')}
            </Button>
            <Button
              onClick={onSave}
              className="tw:dark:bg-neutral-900 tw:dark:text-white"
            >
              <SvgIcon name="checkmark" size={20} />
              {i18n.t('EventOrder:updateOrder')}
            </Button>
          </>
        ),
        className: 'tw:bg-primary-ultralight',
      };
    }

    return {
      text: i18n.t('EventOrder:orderUpToDate') || 'Order is up to date',
      icon: <SvgIcon name="checkmark" size={20} />,
      button: (
        <Button
          variant="outline"
          onClick={onCancel}
          className="tw:dark:bg-neutral-900 tw:dark:text-white"
        >
          {i18n.t('Common:cancel')}
        </Button>
      ),
      className: 'tw:bg-gray-50',
    };
  }, [status, onCancel, onSave]);

  return (
    <EventOrderBottomBar
      className={content.className}
      icon={content.icon}
      text={content.text}
    >
      {content.button}
    </EventOrderBottomBar>
  );
}

export function EditEventMerchantOrder() {
  const history = useHistory();
  const isMobile = useIsMobile();
  const {
    order,
    loadingStatus,
    eventName,
    eventId,
    eventAccountId,
    eventAccountName,
    eventAccountEmail,
    submitForm,
    sendSalesOrder,
    resetForm,
    hasOrderChanged,
    updateOrder,
    updateItemTypeOrder,
    formControl,
    hasOpsOrder,
    orderStatus,
    setOrderStatus,
    salesOrderUrl,
    salesOrderZohoId,
    salesOrderSendReferenceId,
    eventStartDate,
    eventEndDate,
    formDeadline,
    eventManager,
    currency,
    taxPercentage,
    minimumCleaningFeeRate,
    canSendSalesOrder,
    onStatusChangedToApproved,
  } = useEditEventMerchantOrderContext();

  // State for success modal
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Register callback for when status changes to approved
  useEffect(() => {
    onStatusChangedToApproved(() => {
      setShowSuccessModal(true);
    });
  }, [onStatusChangedToApproved]);

  // Get disabled statuses based on current status
  const disabledStatuses = useMemo(
    () => getEventOrderDisabledStatuses(orderStatus),
    [orderStatus],
  );

  const isLoading =
    loadingStatus === 'initializing' || loadingStatus === 'loading';
  const isSubmitting = loadingStatus === 'submitting';
  const isError = loadingStatus === 'error';
  const isNotFound = loadingStatus === 'not-found';

  // Derived values for OrderInfo
  const formDeadlineFormatted = formDeadline
    ? new Date(formDeadline).toLocaleString()
    : undefined;

  const isFormExpired = formDeadline
    ? new Date(formDeadline) < new Date()
    : false;

  const eventDates =
    eventStartDate && eventEndDate
      ? `${new Date(eventStartDate).toLocaleDateString()} - ${new Date(eventEndDate).toLocaleDateString()}`
      : undefined;

  if (isNotFound) {
    return (
      <ErrorState
        title={i18n.t('Common:notFound')}
        message={
          i18n.t('Common:notFoundText') || 'The shared link could not be found.'
        }
        onRetry={() => history.push(Routes.orders())}
        retryLabel={i18n.t('MerchantOrder:toOverview')}
      />
    );
  }

  if (isError) {
    return (
      <ErrorState
        title={i18n.t('Common:error') || 'Failed to load order'}
        message="There was an error loading the order. Please try again or contact support."
        onRetry={() => history.push(Routes.orders())}
        retryLabel={i18n.t('MerchantOrder:toOverview')}
      />
    );
  }

  return (
    <div className="tw:space-y-4">
      {/* Header */}
      <div className="tw:flex tw:flex-row tw:justify-between tw:items-center">
        <div className="tw:flex tw:flex-col tw:gap-2">
          <h1 className="tw:text-3xl tw:font-semibold">
            {i18n.t('MerchantOrder:editEventOrder') || 'Edit Event Order'}
          </h1>
          <Skeleton loading={isLoading}>
            <p className="tw:leading-6">
              {eventName
                ? i18n.t('MerchantOrder:editOrderFor', { eventName }) ||
                  `Edit order for ${eventName}`
                : i18n.t('MerchantOrder:editOrderDescription') ||
                  'Update container quantities for this event order'}
            </p>
          </Skeleton>
        </div>
      </div>

      {/* Mobile Order Info */}
      {isMobile && (
        <OrderInfo.Admin
          isLoading={isLoading}
          orderStatus={orderStatus}
          hasOpsOrder={hasOpsOrder}
          formDeadline={formDeadlineFormatted}
          eventName={eventName}
          eventAccountName={eventAccountName}
          eventAccountEmail={eventAccountEmail}
          eventDates={eventDates}
          eventManager={eventManager || undefined}
          eventId={eventId}
          eventAccountId={eventAccountId}
          salesOrderUrl={salesOrderUrl}
          salesOrderZohoId={salesOrderZohoId}
          salesOrderSendReferenceId={salesOrderSendReferenceId}
          canSendSalesOrder={canSendSalesOrder}
          onSendSalesOrder={sendSalesOrder}
          isSubmitting={isSubmitting}
          onOrderStatusChange={setOrderStatus}
          disabledStatuses={disabledStatuses}
        />
      )}

      <OrderFormLayout
        isMobile={isMobile}
        leftColumn={
          isLoading ? (
            <Card>
              <CardHeader>
                <Skeleton loading className="tw:h-6 tw:w-48" />
              </CardHeader>
              <CardContent>
                <Skeleton loading className="tw:h-32 tw:w-full" />
              </CardContent>
            </Card>
          ) : (
            Object.values(order).map((itemLocation) => (
              <SectionCard
                key={itemLocation.itemLocationId}
                title={itemLocation.itemLocationName}
              >
                <ItemLocationOrderSection.Admin
                  itemLocationId={itemLocation.itemLocationId}
                  containers={Object.values(itemLocation.containers)}
                  itemTypes={Object.values(itemLocation.itemTypes)}
                  disabled={isSubmitting}
                  onChangeQuantity={(containerTypeId, quantity) => {
                    updateOrder({
                      itemLocationId: itemLocation.itemLocationId,
                      containerTypeId,
                      quantity,
                    });
                  }}
                  onChangeItemTypeQuantity={(itemTypeId, quantity) => {
                    updateItemTypeOrder({
                      itemLocationId: itemLocation.itemLocationId,
                      itemTypeId,
                      quantity,
                    });
                  }}
                  currency={currency}
                />
              </SectionCard>
            ))
          )
        }
        rightColumn={
          <>
            {/* Order Info (Desktop) */}
            {!isMobile && (
              <OrderInfo.Admin
                isLoading={isLoading}
                orderStatus={orderStatus}
                hasOpsOrder={hasOpsOrder}
                formDeadline={formDeadlineFormatted}
                eventName={eventName}
                eventAccountName={eventAccountName}
                eventAccountEmail={eventAccountEmail}
                eventDates={eventDates}
                eventManager={eventManager || undefined}
                eventId={eventId}
                eventAccountId={eventAccountId}
                salesOrderUrl={salesOrderUrl}
                salesOrderZohoId={salesOrderZohoId}
                salesOrderSendReferenceId={salesOrderSendReferenceId}
                canSendSalesOrder={canSendSalesOrder}
                onSendSalesOrder={sendSalesOrder}
                isSubmitting={isSubmitting}
                onOrderStatusChange={setOrderStatus}
                disabledStatuses={disabledStatuses}
              />
            )}

            {/* Cost Summary */}
            <CostSummary.ConnectedBase
              currency={currency}
              order={order}
              taxPercentage={taxPercentage}
              minimumCleaningFeeRate={minimumCleaningFeeRate}
            >
              {/* Comment Field */}
              <OrderField.Comment
                control={formControl}
                inputClassName="tw:min-h-[80px]"
              />
            </CostSummary.ConnectedBase>
          </>
        }
      />

      {/* Bottom Bar */}
      {!isLoading && !isError && !isNotFound && (
        <BottomBar>
          <EditBottomBar
            isSubmitting={isSubmitting}
            hasOrderChanged={hasOrderChanged}
            onCancel={resetForm}
            onSave={submitForm}
          />
        </BottomBar>
      )}

      {/* Success Modal for Approved Status */}
      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent className="tw:max-w-[500px]">
          <DialogHeader>
            <div className="tw:flex tw:items-center tw:gap-3 tw:mb-2">
              <FaCheckCircle className="tw:text-green-500 tw:dark:text-green-400 tw:text-3xl tw:flex-shrink-0" />
              <DialogTitle>
                {i18n.t('MerchantOrder:salesOrderCreatedTitle') ||
                  'Zoho Sales Order Successfully Created'}
              </DialogTitle>
            </div>
            <DialogDescription>
              {i18n.t('MerchantOrder:salesOrderCreatedMessage') ||
                'The Zoho Sales Order was successfully created:'}
            </DialogDescription>
          </DialogHeader>

          <div className="tw:py-4">
            {/* Sales Order Link */}
            {salesOrderUrl && (
              <div className="tw:mb-4">
                <a
                  href={salesOrderUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tw:text-blue-600 tw:hover:text-blue-700 tw:dark:text-blue-400 tw:dark:hover:text-blue-300 tw:underline tw:font-medium"
                >
                  {i18n.t('MerchantOrder:salesOrderLink') || 'Zoho Sales Order'}
                  {salesOrderZohoId && ` (${salesOrderZohoId})`}
                </a>
              </div>
            )}

            {/* Next Steps */}
            <div className="tw:space-y-3">
              <h4 className="tw:font-semibold tw:text-sm tw:text-gray-900 tw:dark:text-gray-100">
                {i18n.t('MerchantOrder:nextStepsTitle') || 'Next Steps:'}
              </h4>
              <ol className="tw:list-decimal tw:list-inside tw:space-y-2 tw:text-sm tw:text-gray-700 tw:dark:text-gray-300">
                <li>
                  {i18n.t('MerchantOrder:nextStep1') ||
                    'Manually edit the sales order in Zoho if necessary'}
                </li>
                <li>
                  {i18n.t('MerchantOrder:nextStep2') ||
                    'Mark the Sales Order as confirmed in Zoho Books'}
                </li>
                <li>
                  {i18n.t('MerchantOrder:nextStep3') ||
                    'Come back to the Event Order and press the "Send Sales Order" button to send the Sales Order out'}
                </li>
              </ol>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={() => setShowSuccessModal(false)}>
              {i18n.t('Common:close') || 'Close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
